import { ChangeDetectionStrategy, Component, computed, inject, viewChild } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { ProjectService } from 'src/app/core/services/project.service';
import { LoaderComponent } from 'src/app/shared/loading/loader/loader.component';
import { NotificationDataService } from 'src/app/shared/notification/notification-data.service';
import { NotificationEmptyComponent } from 'src/app/shared/notification/notification-empty/notification-empty.component';
import { NotificationEntryDto } from 'src/app/shared/notification/state/notification.store';
import { getDeliverableIcon } from 'src/app/shared/notification/utils/getDeliverableIcon';
import { getDeliverableRouterLink } from 'src/app/shared/notification/utils/getDeliverableRouterLink';
import {
  CompactNotification,
  NotificationCompactComponent,
} from '../../../shared/notification/notification-compact/notification-compact.component';

type CompactNotificationWithLink = CompactNotification & {
  link: { route: (string | number)[]; queryParams: {} };
};

const MAX_BADGE_NOTIFICATIONS = 9;

@Component({
  selector: 'app-notification-menu',
  imports: [
    MatButtonModule,
    MatBadgeModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    NotificationCompactComponent,
    NotificationEmptyComponent,
    RouterLink,
    LoaderComponent,
  ],
  template: `
    <button mat-icon-button [matMenuTriggerFor]="notificationMenu">
      <mat-icon
        size="20px"
        aria-hidden="false"
        svgIcon="notification"
        [matBadge]="numberOfUnread()"
        matBadgePosition="above after"
      />
    </button>

    <!-- Menu Panel -->
    <mat-menu
      #notificationMenu="matMenu"
      class="notification-menu-panel"
      backdropClass="notification-custom_backdrop"
    >
      <div
        class="notifications-header flex__justify-between flex__align-center padding-vertical--half padding-horizontal--one"
      >
        <span (click)="onClickTitle()" title="Click to refresh"><h5>My latest notifications</h5></span>
        <button mat-icon-button (click)="menuTrigger()?.closeMenu()">
          <mat-icon svgIcon="close" />
        </button>
      </div>
      <mat-divider />

      @if (notifications().length < 1 || isLoading()) {
        <div class="notifications-notice flex__column flex__justify-center flex__align-center">
          @if (isLoading()) {
            <app-loader [size]="40"></app-loader>
          } @else {
            <app-notification-empty />
          }
        </div>
      } @else {
        <div class="notifiactions-wrapper">
          @for (notification of notifications(); track notification.id) {
            <a
              mat-menu-item
              class="menu-item"
              [routerLink]="notification.link.route"
              [queryParams]="notification.link.queryParams"
              (click)="onClickNotification(notification)"
            >
              <app-notification-compact [notification]="notification" />
            </a>
          }
        </div>
      }
      <a [routerLink]="notificationsLink()" class="show-all-wrapper" (click)="menuTrigger()?.closeMenu()">
        <button mat-button color="tonal">Show all my notifications</button>
      </a>
    </mat-menu>
  `,
  styleUrl: './notification-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationMenuComponent {
  readonly #projectService = inject(ProjectService);
  readonly #notificationDataService = inject(NotificationDataService);

  readonly menuTrigger = viewChild(MatMenuTrigger);

  readonly notifications = computed(() => {
    const notifications = this.#notificationDataService.notificationStore.notifications();
    return notifications.map(toCompactNotification);
  });
  readonly numberOfUnread = computed(() => {
    const nNotifications = this.notifications().filter(onlyUnread).length;
    return nNotifications > MAX_BADGE_NOTIFICATIONS ? '+9' : nNotifications > 0 ? nNotifications : '';
  });
  readonly isLoading = computed(() => this.#notificationDataService.notificationStore.isLoading());

  readonly notificationsLink = computed(
    () => `/projects/${this.#projectService.currentProjectId()}/notifications`,
  );

  onClickTitle() {
    this.#notificationDataService.notificationStore.loadAll(this.#projectService.currentProjectId());
  }

  onClickNotification(notification: CompactNotificationWithLink) {
    this.#notificationDataService.markAsSeen(notification);
    this.menuTrigger()?.closeMenu();
  }
}

function toCompactNotification(dto: NotificationEntryDto): CompactNotificationWithLink {
  return {
    date: new Date(dto.timestamp),
    icon: getDeliverableIcon(dto.deliverableType),
    id: dto.id,
    metaData: dto.discipline,
    seen: dto.seen,
    subtitle: dto.project,
    title: dto.deliverableIdText,
    link: getDeliverableRouterLink(dto),
    emailSent: dto.emailSent,
  };
}

function onlyUnread(value: CompactNotification): value is CompactNotification {
  return !value.seen;
}
